import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import i18next from 'i18next';

import { ComponentRegistry } from 'core/index';
import { DefaultItemComponent } from 'legacyComponents/itemComponent';
import { Field } from 'libs/forms/views/field';
import { ArrayFieldStore } from 'libs/forms/fieldStores';
import { Validation } from 'libs/forms/views/validation';
import { Remainder } from 'libs/forms/views/remainder';
import {
  ItemsWrapper,
  WrappedInput,
  Placeholder,
  SpinnerStyled,
  CreateNew,
  AutocompleteInputDivider as Divider,
} from 'libs/forms/styles';

@observer
export class ListField extends Field {
  static defaultStore = ArrayFieldStore;

  componentDidMount() {
    if (this.props.fieldStore.autoFocus) {
      this._input.focus();
    }
  }

  updateInputValue = e => {
    const { fieldStore } = this.props;
    if (fieldStore.conf.autocomplete?.toLowerCase) {
      // NOTE: ensure value is lowercased to highlight match appropriately
      fieldStore.UpdateInputValue(e.target.value.toLowerCase());
    } else {
      fieldStore.UpdateInputValue(e.target.value);
    }
  };

  onKeyDown = e => {
    // override this.onKeyDown using a ref when autocomplete is active
    if (e.which === 13 || e.which === 9 || e.which === 188) {
      e.preventDefault();
      this.props.fieldStore.Add();
      this.focusNext(e);
    }
  };

  clickDeleteButton = item => {
    this._input.focus();
    const { fieldStore } = this.props;
    fieldStore.Delete(item);
  };

  focusNext = () => {
    const {
      fieldStore: { value, conf },
    } = this.props;
    if (conf.maxLen && conf.maxLen === value.length) {
      const fs = this.props.fieldStore;
      const nextInput = document.querySelector(
        `#${fs.formStore.formName}-${fs.nextFieldName}`,
      );
      if (nextInput) {
        nextInput.focus();
      } else {
        const currInput = document.querySelector(
          `#${fs.formStore.formName}-${fs.fieldName}`,
        );
        currInput.blur();
      }
    }
  };

  render() {
    const {
      fieldStore,
      noValidationIcon,
      responsive,
      inputAlwaysVisible = false,
    } = this.props;
    const { maxLen, itemName, itemComponent, appKey } = fieldStore.conf;
    const items = [];
    let ItemComponent = ComponentRegistry.get(itemComponent);
    if (!itemComponent) {
      ItemComponent = DefaultItemComponent;
      // console.warn(`No itemComponent ${itemComponent} exists in the ComponentRegistry`);
    }
    let deletedItemCount = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const item of fieldStore.value) {
      if (item.deleted) {
        deletedItemCount += 1;
        // eslint-disable-next-line no-continue
        continue;
      }
      items.push(
        <ItemComponent
          deleteHandler={() => this.clickDeleteButton(item)}
          key={item[appKey] || item}
          item={item}
          fieldStore={fieldStore}
        />,
      );
    }
    const itemCount = fieldStore.value.length - deletedItemCount;
    const remainder = fieldStore.inputValue && (
      <Remainder
        fieldStore={fieldStore}
        itemCount={itemCount}
        maxLen={maxLen}
        itemName={itemName}
        show={fieldStore.inputValue.length}
      />
    );
    const addItemVisible =
      inputAlwaysVisible ||
      ((!maxLen || itemCount < maxLen) &&
        (fieldStore.inputValue?.length || fieldStore.isFocussed));

    const divider =
      addItemVisible && (fieldStore.inputValue?.length || itemCount !== 0) ? (
        <Divider lessMargin={itemCount > 0} />
      ) : null;

    let spinner = null;

    if (fieldStore.optionsManager && fieldStore.optionsManager.loading) {
      spinner = <SpinnerStyled size='small' />;
    }

    if (
      fieldStore.inputValue?.length > 1 &&
      fieldStore.conf.autocomplete?.allowNew &&
      fieldStore.optionsManager.selectables?.length === 0
    ) {
      spinner = (
        <CreateNew>
          {fieldStore.conf.newItemPrompt
            ? fieldStore.conf.newItemPrompt
            : `(Enter or Tab to create this ${fieldStore.conf.itemName})`}
        </CreateNew>
      );
    }

    return (
      <ItemsWrapper
        hasItems={itemCount > 0}
        key={fieldStore.fieldName}
        focussed={fieldStore.isFocussed}
        onTouchStart={e => {
          e.preventDefault();
          e.stopPropagation();
          this._input.focus();
        }}
        onMouseDown={e => {
          e.preventDefault();
          e.stopPropagation();
          this._input.focus();
        }}
        responsive={responsive}
      >
        {items}
        {remainder}
        {divider}
        <WrappedInput
          id={this.inputId}
          visible={addItemVisible}
          autoFocus={itemCount > 0 ? fieldStore.autoFocus : false}
          placeholder={
            fieldStore.conf.placeholder &&
            i18next.t(fieldStore.conf.placeholder)
          }
          value={
            fieldStore.conf.prefixValue
              ? fieldStore.conf.prefixValue + fieldStore.inputValue
              : fieldStore.inputValue
          }
          posting={fieldStore.formStore.posting}
          ref={input => {
            this._input = input;
          }}
          onFocus={this.onFocus}
          onBlur={e => {
            this.onBlur();
          }}
          onChange={this.updateInputValue}
          onKeyDown={this.onKeyDown}
          autoComplete='off'
          responsive={responsive}
        />
        {spinner}
        {!addItemVisible && itemCount === 0 ? (
          <Placeholder>{i18next.t(fieldStore.conf.placeholder)}</Placeholder>
        ) : (
          ''
        )}
        {noValidationIcon ? (
          ''
        ) : (
          <Validation
            fieldStore={fieldStore}
            handleClick={this.focusAfterDelete}
          />
        )}
      </ItemsWrapper>
    );
  }
}

ListField.propTypes = {
  fieldStore: PropTypes.object,
};
